/**
 * 검색어 동의어 매핑 시스템
 * 카테고리와 지역명에 대한 동의어/유사어를 관리합니다.
 */

// 카테고리 동의어 매핑
export const categoryMap: Record<string, string[]> = {
  '베이커리': ['빵집', '빵', 'bakery', '베이커', '베이크', '제과', '제빵'],
  '카페': ['커피숍', '커피', 'cafe', 'coffee', '카페'],
  '치킨': ['닭', '후라이드', 'chicken', '치킨', '통닭', '닭고기'],
  '피자': ['pizza', '피자집', '피자', '피짜'],
  '한식': ['korean', '한국음식', '한식당', '한국', '한식집'],
  '중식': ['chinese', '중국음식', '중국집', '짜장면', '짬뽕', '중화요리', '중국'],
  '일식': ['japanese', '일본음식', '일본집', '스시', '초밥', '일본', '돈까스'],
  '양식': ['western', '서양음식', '양식당', '파스타', '스테이크'],
  '분식': ['snack', '떡볶이', '김밥', '순대', '튀김', '분식집'],
  '패스트푸드': ['fastfood', '버거', '햄버거', '맥도날드', '버거킹', 'kfc'],
  '도시락': ['lunchbox', '도시락집', '벤토', '점심'],
  '샐러드': ['salad', '샐러드바', '건강식'],
  '김밥': ['kimbap', '김밥집', '김밥천국'],
  '뷔페': ['buffet', '부페', '무한리필'],
  '디저트': ['dessert', '케이크', '마카롱', '쿠키', '과자', '달콤한'],
  '아시안': ['asian', '아시아', '동남아', '베트남', '태국'],
};

// 지역 동의어 매핑
export const locationMap: Record<string, string[]> = {
  '홍대': ['홍익대', '마포구', '서교동', '합정', '상수', '홍대입구'],
  '강남': ['강남구', '역삼', '논현', '삼성동', '테헤란로', '강남역', '선릉'],
  '신촌': ['신촌동', '서대문구', '연세대', '이대', '신촌역'],
  '이태원': ['용산구', '이태원동', '한남동', '이태원역'],
  '명동': ['중구', '명동역', '충무로'],
  '종로': ['종로구', '종각', '광화문', '인사동', '종로3가'],
  '서초': ['서초구', '서초동', '교대', '방배'],
  '송파': ['송파구', '잠실', '석촌', '문정', '가락'],
  '성수': ['성수동', '성동구', '뚝섬', '서울숲'],
  '을지로': ['을지로입구', '을지로3가', '을지로4가'],
  '건대': ['건국대', '건대입구', '화양동', '광진구'],
  '대학로': ['혜화', '혜화동', '성균관대'],
  '여의도': ['영등포구', '여의도동', '여의나루'],
  '판교': ['분당구', '판교역', '판교테크노밸리'],
};

/**
 * 검색어를 동의어로 확장합니다.
 * @param term 원본 검색어
 * @returns 확장된 검색어 배열 (중복 제거됨)
 */
export function expandSearchTerm(term: string): string[] {
  const normalizedTerm = term.toLowerCase().trim();
  const expanded = new Set<string>([term]); // 원본 검색어 포함
  
  // 카테고리 동의어 확장
  for (const [key, synonyms] of Object.entries(categoryMap)) {
    const normalizedKey = key.toLowerCase();
    const normalizedSynonyms = synonyms.map(s => s.toLowerCase());
    
    // 키나 동의어에 검색어가 포함되어 있는지 확인
    if (normalizedKey.includes(normalizedTerm) || 
        normalizedSynonyms.some(s => s.includes(normalizedTerm))) {
      expanded.add(key);
      synonyms.forEach(s => expanded.add(s));
    }
    
    // 검색어가 키나 동의어를 포함하는지 확인
    if (normalizedTerm.includes(normalizedKey) ||
        normalizedSynonyms.some(s => normalizedTerm.includes(s))) {
      expanded.add(key);
      synonyms.forEach(s => expanded.add(s));
    }
  }
  
  // 지역 동의어 확장
  for (const [key, synonyms] of Object.entries(locationMap)) {
    const normalizedKey = key.toLowerCase();
    const normalizedSynonyms = synonyms.map(s => s.toLowerCase());
    
    // 키나 동의어에 검색어가 포함되어 있는지 확인
    if (normalizedKey.includes(normalizedTerm) || 
        normalizedSynonyms.some(s => s.includes(normalizedTerm))) {
      expanded.add(key);
      synonyms.forEach(s => expanded.add(s));
    }
    
    // 검색어가 키나 동의어를 포함하는지 확인
    if (normalizedTerm.includes(normalizedKey) ||
        normalizedSynonyms.some(s => normalizedTerm.includes(s))) {
      expanded.add(key);
      synonyms.forEach(s => expanded.add(s));
    }
  }
  
  return Array.from(expanded);
}

/**
 * 여러 검색어를 동의어로 확장합니다.
 * @param terms 검색어 배열
 * @returns 확장된 검색어 배열 (중복 제거됨)
 */
export function expandMultipleTerms(terms: string[]): string[] {
  const allExpanded = new Set<string>();
  
  terms.forEach(term => {
    const expanded = expandSearchTerm(term);
    expanded.forEach(e => allExpanded.add(e));
  });
  
  return Array.from(allExpanded);
}